import { Link } from "react-router-dom";
import React from "react";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink } from "@fortawesome/free-solid-svg-icons";
import { useTheme } from "../../contexts/ThemeContext";
import { Theme } from "../../theme";

// アニメーション
const slideIn = keyframes`
	from {
		opacity: 0;
		transform: translateX(-10px);
	}
	to {
		opacity: 1;
		transform: translateX(0);
	}
`;

// Styled Components - 参照される順に定義
const Header = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: baseline;
	gap: 1rem;
	margin-bottom: 0.5rem;
	flex-wrap: wrap;
`;

const Title = styled.h4<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.primary};
	font-size: 1.125rem;
	font-weight: 700;
	margin: 0;
	white-space: pre-line;
	line-height: 1.4;
	transition: all 0.2s ease;

	@media (max-width: 768px) {
		font-size: 1rem;
	}
`;

const Terms = styled.span<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.tertiary};
	font-size: 0.875rem;
	font-weight: 500;
	white-space: nowrap;
`;

const Description = styled.p<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.secondary};
	font-size: 0.95rem;
	line-height: 1.6;
	margin: 0 0 0.625rem;
	white-space: pre-line;

	@media (max-width: 768px) {
		font-size: 0.9rem;
	}
`;

const ProjectLinkIcon = styled.span`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 1.25rem;
	height: 1.25rem;
	transition: transform 0.3s ease;
`;

const ProjectLink = styled.div<{ theme: Theme }>`
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	color: ${props => props.theme.colors.primary.main};
	font-weight: 600;
	font-size: 0.9rem;
	transition: all 0.2s ease;
`;

const Container = styled.div<{ theme: Theme }>`
	background: ${props => props.theme.colors.background.secondary};
	border: 1px solid ${props => props.theme.colors.neutral[200]};
	border-radius: ${props => props.theme.density.cardRadius};
	padding: ${props => props.theme.density.cardPadding};
	transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
	animation: ${slideIn} 0.3s ease-out;
	position: relative;
	overflow: hidden;
	box-shadow: ${props => props.theme.shadows.base};

	a {
		text-decoration: none;
		display: block;
	}

	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		width: 3px;
		height: 0;
		background: ${props => props.theme.colors.primary.gradient};
		transition: height 0.2s ease;
	}

	&:hover {
		box-shadow: ${props => props.theme.shadows.lg};
		transform: translateX(3px);
		border-color: ${props => props.theme.colors.primary.main};

		&::before {
			height: 100%;
		}

		${Title} {
			color: ${props => props.theme.colors.primary.main};
		}

		${ProjectLink} {
			color: ${props => props.theme.colors.primary.light};
			transform: translateX(4px);
		}

		${ProjectLinkIcon} {
			transform: rotate(-45deg);
		}
	}

	@media (max-width: 768px) {
		padding: ${props => props.theme.density.cardPaddingMobile};
	}
`;

interface SubworkProjectProps {
	logo?: string | null;
	title: string;
	terms?: string;
	description: string;
	linkText: string;
	link: string;
}

const SubworkProject: React.FC<SubworkProjectProps> = (props) => {
	const { title, terms, description, linkText, link } = props;
	const { theme } = useTheme();

	return (
		<Container theme={theme}>
			<Link to={link}>
				<div>
					<Header>
						<Title theme={theme}>{title}</Title>
						{terms && <Terms theme={theme}>{terms}</Terms>}
					</Header>
					<Description theme={theme}>{description}</Description>
					{link && (
						<ProjectLink theme={theme}>
							<ProjectLinkIcon>
								<FontAwesomeIcon icon={faLink} />
							</ProjectLinkIcon>
							<span>{linkText}</span>
						</ProjectLink>
					)}
				</div>
			</Link>
		</Container>
	);
};

export default SubworkProject;
